import axios from "axios";
import {
  CreateProductImageUploadUrlDtoContentTypeEnum,
  type CreateProductImageUploadUrlDto,
} from "../../../api";
import { productImagesService } from "../../api_client";

const ALLOWED_CONTENT_TYPES = new Set<CreateProductImageUploadUrlDtoContentTypeEnum>([
  CreateProductImageUploadUrlDtoContentTypeEnum.ImageJpeg,
  CreateProductImageUploadUrlDtoContentTypeEnum.ImagePng,
  CreateProductImageUploadUrlDtoContentTypeEnum.ImageWebp,
]);

const isAllowedContentType = (
  type: string,
): type is CreateProductImageUploadUrlDtoContentTypeEnum =>
  ALLOWED_CONTENT_TYPES.has(type as CreateProductImageUploadUrlDtoContentTypeEnum);

export class ProductImageUploadError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = "ProductImageUploadError";
  }
}

/**
 * Uploads a single image to GCS via the backend's signed-URL flow.
 *
 *   1. POST /product-images/upload-url → { uploadUrl, headers, objectKey }
 *   2. PUT the raw binary directly to GCS using `uploadUrl`
 *   3. Return `objectKey` — the caller stores it on CreateProductVariantDto.image
 *
 * In mock mode MSW intercepts both calls, so the same path works without GCS.
 */
export interface UploadProductImageOptions {
  scope?: string;
  /** Called repeatedly during the PUT with a percentage (0..100). */
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

export async function uploadProductImage(
  file: File,
  options?: UploadProductImageOptions,
): Promise<string> {
  if (!isAllowedContentType(file.type)) {
    throw new ProductImageUploadError(
      `Unsupported image type: ${file.type || "unknown"}. Use JPEG, PNG, or WebP.`,
    );
  }

  const dto: CreateProductImageUploadUrlDto = {
    contentType: file.type,
    fileSize: file.size,
    ...(options?.scope ? { scope: options.scope } : {}),
  };

  let urlRes;
  try {
    urlRes = await productImagesService.createUploadUrl(dto);
  } catch (err) {
    throw new ProductImageUploadError("Could not get upload URL", err);
  }

  const { uploadUrl, headers, objectKey } = urlRes.data;

  try {
    await axios.put(uploadUrl, file, {
      headers: { ...headers, "Content-Type": file.type },
      // Don't ship cookies/tokens to a third-party host.
      withCredentials: false,
      transformRequest: [(d) => d],
      signal: options?.signal,
      onUploadProgress: (e) => {
        if (!options?.onProgress) return;
        const total = e.total ?? file.size;
        if (!total) return;
        options.onProgress(Math.min(100, Math.round((e.loaded / total) * 100)));
      },
    });
  } catch (err) {
    throw new ProductImageUploadError("Could not upload image to storage", err);
  }

  return objectKey;
}
